 <template>
  <div>
    <ModalLibroMayorDetalle :listDetalle="listDetalle" :visible="visible" @update:visible="visible = $event" />

    <div class="p-6 max-w-5xl mx-auto">
      <!-- Header con gradiente moderno -->
      <div class="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 p-6 mb-6 shadow-xl">
        <div class="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
        <div class="relative z-10">
          <span class="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full text-white/90 text-xs font-mono mb-3">
            &#60;ListLibroMayor&#62;
          </span>
          <h2 class="text-2xl font-bold text-white flex items-center gap-3">
            <span class="bg-white/20 p-2 rounded-xl backdrop-blur-sm">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
            </span>
            Gastos extraídos de archivos CSV libro mayor
          </h2>
          <p class="text-white/80 text-sm mt-1">
            Historial de importaciones de libros mayores
          </p>
        </div>
      </div>

      <!-- Contenedor de tabla -->
      <div class="bg-white/80   rounded-2xl shadow-2xl border border-white/20 overflow-hidden">
        <!-- Encabezado de tabla con contador -->
        <div class="flex flex-wrap items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white border-b border-gray-200">
          <div class="flex items-center gap-3">
            <span class="text-2xl">📊</span>
            <h4 class="font-semibold text-gray-800">Registros importados</h4>
            <span class="text-xs bg-indigo-100 text-indigo-600 px-2.5 py-1 rounded-full font-medium">
              {{ historialLibros ? historialLibros.length : 0 }} registros
            </span>
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-500">
            <span class="w-2 h-2 rounded-full bg-green-400 animate-pulse"></span>
            Datos actualizados
          </div>
        </div>

        <!-- Scroll de tabla -->
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-gray-700">
            <thead class="bg-gradient-to-r from-gray-100 to-gray-50 sticky top-0">
              <tr class="border-b border-gray-200">
                <th class="text-center py-3 px-2 text-xs font-mono uppercase tracking-wider text-gray-600">ID</th>
                <th class="text-center py-3 px-2 text-xs font-mono uppercase tracking-wider text-gray-600">Fecha/Hora de carga</th>
                <th class="text-center py-3 px-2 text-xs font-mono uppercase tracking-wider text-gray-600">Descripción</th>
                <th class="text-center py-3 px-2 text-xs font-mono uppercase tracking-wider text-gray-600">Leídos</th>
                <th class="text-center py-3 px-2 text-xs font-mono uppercase tracking-wider text-gray-600">Creados</th>
                <th class="text-center py-3 px-2 text-xs font-mono uppercase tracking-wider text-gray-600">Actualizados</th>
                <th class="text-center py-3 px-2 text-xs font-mono uppercase tracking-wider text-gray-600">Erróneos</th>
                <th class="text-center py-3 px-2 text-xs font-mono uppercase tracking-wider text-gray-600" colspan="2">Acciones</th>
              </tr>
            </thead>

            <tbody>
              <tr 
                v-for="item in historialLibros" 
                :key="item.id"
                class="border-b border-gray-100 hover:bg-indigo-50/50 transition-colors duration-200"
              >
                <!-- ID -->
                <td class="py-4 px-2 text-center">
                  <span class="inline-block px-2 py-1 text-xs font-mono font-semibold text-indigo-600 bg-indigo-50 rounded-lg">
                    #{{ item.id }}
                  </span>
                </td>

                <!-- Timestamp -->
                <td class="py-4 px-2 text-center">
                  <div class="text-xs font-mono text-gray-600">
                    {{ item.timestamp }}
                  </div>
                </td>

                <!-- Descripción -->
                <td class="py-4 px-2 text-center max-w-[200px]">
                  <span class="text-sm text-gray-700" :title="item.descripcion">
                    {{ item.descripcion ? item.descripcion.substring(0, 30) + (item.descripcion.length > 30 ? '...' : '') : '-' }}
                  </span>
                </td>

                <!-- Registros leídos -->
                <td class="py-4 px-2 text-center">
                  <span class="font-mono font-medium text-gray-700">{{ item.registros_leidos || 0 }}</span>
                </td>

                <!-- Registros creados -->
                <td class="py-4 px-2 text-center">
                  <span class="font-mono font-medium text-green-600">{{ item.registros_creados || 0 }}</span>
                </td>

                <!-- Registros actualizados -->
                <td class="py-4 px-2 text-center">
                  <span class="font-mono font-medium text-blue-600">{{ item.registros_actualizados || 0 }}</span>
                </td>

                <!-- Registros erróneos -->
                <td class="py-4 px-2 text-center">
                  <span class="font-mono font-medium" :class="item.registros_erroneos > 0 ? 'text-red-600' : 'text-gray-400'">
                    {{ item.registros_erroneos || 0 }}
                  </span>
                </td>

                <!-- Botón Borrar -->
                <td class="py-4 px-2 text-center">
                  <button
                    @click="borrar(item.id)"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-all duration-200 border border-red-200 hover:border-red-300 text-xs font-medium"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 7h12M9 7V4h6v3m-7 4v6m4-6v6m4-10v10a2 2 0 01-2 2H8a2 2 0 01-2-2V7h12z" />
                    </svg>
                    Borrar
                  </button>
                </td>

                <!-- Botón Editar -->
                <td class="py-4 px-2 text-center">
                  <button
                    @click="editar(item.id)"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-all duration-200 border border-blue-200 hover:border-blue-300 text-xs font-medium"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15.232 5.232l3.536 3.536M9 11l6-6 3 3-6 6H9v-3z" />
                    </svg>
                    Editar
                  </button>
                </td>
              </tr>

              <!-- Fila vacía cuando no hay datos -->
              <tr v-if="!historialLibros || historialLibros.length === 0">
                <td colspan="9" class="text-center py-12">
                  <div class="flex flex-col items-center gap-3">
                    <span class="text-6xl">📭</span>
                    <p class="text-gray-400 font-medium">No hay registros de importación</p>
                    <p class="text-xs text-gray-300">Los libros mayores importados aparecerán aquí</p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pie de tabla con estadísticas -->
        <div v-if="historialLibros && historialLibros.length > 0" class="flex flex-wrap items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white border-t border-gray-200">
          <div class="flex items-center gap-4 text-xs text-gray-500">
            <span>📋 Total: <span class="font-semibold text-gray-700">{{ historialLibros.length }}</span> registros</span>
            <span class="w-px h-4 bg-gray-300"></span>
            <span>📅 Última importación: <span class="font-semibold text-gray-700">{{ historialLibros[0]?.timestamp || '-' }}</span></span>
          </div>
          <div class="flex items-center gap-2 text-xs text-gray-400">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Datos en tiempo real
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import api from "@/api/axios";
import ModalLibroMayorDetalle from "./ModalLibroMayorDetalle.vue";
export default {
  name: "ListLibroMayorGeneral",
  components:{
    ModalLibroMayorDetalle
  },
  props: {
    datosModificacion: {
      type: Object,
    },

    timestamp: {
      type: String,
    },
  },
  computed: {
    mesTexto() {
      const meses = [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Septiembre",
        "Octubre",
        "Noviembre",
        "Diciembre",
      ];
      return this.mes > 0 ? meses[this.mes - 1] : "";
    },
  },
  watch: {
    timestamp: function () {
      this.getListLibros();
    },
  },
  methods: {
    async getListLibros() {
      try {
        const resp = await api.get("/api/v1/libromayor/");

        this.historialLibros = resp.data.librosgenerales;
      } catch (error) {
        console.error("Error al enviar:", error);
        this.uploadProgressGlosa = "Error al subir archivo";
      }
    },
    async editar(id) {
      console.log("id", id);
      const formData = new FormData();
      formData.append("libromayorgeneral_id", id);
      try {
        const resp = await api.post("/api/v1/libromayordetalle/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (resp.data.error) {
          alert(resp.data.error);

          return;
        }

        this.loading = false;
        this.listDetalle = resp.data.librosdetalles;
        this.visible=true;

      } catch (error) {
        const status = error.response.status;
        const message = error.response.data?.detail;
        console.error("Error al enviar:", error);
        console.log("Código:", status);
        console.log("Mensaje:", message);
      }
    },
    async borrar(id) {
      if (!confirm('¿Confirma que desea borrar este registro?')){
        return false;
      }
      const formData = new FormData();
      formData.append("libromayorgeneral_id", id);
      try {
        const resp = await api.post("/api/v1/libromayor/delete/", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        if (resp.data.error) {
          alert(resp.data.error);

          return;
        }

        // 🔥  eliminamos el registro del array
        this.historialLibros = this.historialLibros.filter(
          item => item.id !== id
        ); 


      } catch (error) {
        const status = error.response.status;
        const message = error.response.data?.detail;
        console.error("Error al enviar:", error);
        console.log("Código:", status);
        console.log("Mensaje:", message);
      }
    },
  },
 data() {
    return {
      loading: false,
      historialLibros:[],
      listDetalle: [],
      visible :false
    };
 
  },
  mounted() {
    console.log("montar2");
    this.getListLibros();
  },
};
</script>
